<template>
    <div>
      <AdminHeader />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">User List</h5>
          <p class="page-sub">Manage all users and their permissions</p>
        </div>
        <div class="page-actions">
  
          <div class="search-wrap">
            <font-awesome-icon icon="search" class="search-icon" />
            <input type="text" class="search-input" placeholder="Search users..." v-model="query" />
          </div>
  
          <div class="dropdown">
            <button class="btn-outline dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
              <font-awesome-icon icon="download" /> Export
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              <li>
                <a class="dropdown-item" href="#" @click.prevent="openExportModal('pdf')">
                  <font-awesome-icon icon="file-pdf" class="me-2 text-danger" /> Export as PDF
                </a>
              </li>
              <li>
                <a class="dropdown-item" href="#" @click.prevent="openExportModal('csv')">
                  <font-awesome-icon icon="file-csv" class="me-2 text-success" /> Export as CSV
                </a>
              </li>
            </ul>
          </div>
  
          <router-link
            :to="$url('/create-user')"
            class="btn-primary-action"
            :class="permission.create_employee == 0 ? 'disabled' : ''"
          >
            <font-awesome-icon icon="plus" /> Add User
          </router-link>
  
          <button
            class="btn-danger-action"
            data-bs-toggle="modal"
            data-bs-target="#bulkModal"
            :disabled="permission.delete_employee == 0 || selectedModel.length === 0"
          >
            <font-awesome-icon icon="trash" /> Bulk Delete
            <span v-if="selectedModel.length > 0" class="badge-count">{{ selectedModel.length }}</span>
          </button>
  
        </div>
      </div>
  
      <!-- Card -->
      <div class="data-card">
  
        <!-- Loading -->
        <div v-if="loading" class="state-block">
          <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
          <p class="mt-3 text-muted">Loading users...</p>
        </div>
  
        <!-- No Permission -->
        <div v-else-if="permission_status == 403 && !is_permission" class="state-block">
          <div class="state-icon state-icon--red">
            <font-awesome-icon icon="lock" />
          </div>
          <p class="state-title">Access Denied</p>
          <p class="state-sub">You don't have permission to access this resource.</p>
        </div>
  
        <!-- Empty -->
        <div v-else-if="details.length === 0" class="state-block">
          <div class="state-icon state-icon--blue">
            <font-awesome-icon icon="users" />
          </div>
          <p class="state-title">No users yet</p>
          <p class="state-sub">Add your first user to get started.</p>
          <router-link
            v-if="permission.create_employee != 0"
            :to="$url('/create-user')"
            class="btn-primary-action mt-2"
          >
            <font-awesome-icon icon="plus" /> Add User
          </router-link>
        </div>
  
        <!-- Table -->
        <template v-else>
  
          <div class="table-toolbar">
            <span class="table-count">
              Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong> of <strong>{{ pagination.total }}</strong> users
            </span>
            <div class="d-flex align-items-center gap-2">
              <span class="table-count">Rows per page:</span>
              <select v-model.number="tableData.length" @change="tableData.page = 1; fetchData()" class="rows-select">
                <option v-for="n in [5,10,20,50,100,200,500,1000]" :key="n" :value="n">{{ n }}</option>
              </select>
            </div>
          </div>
  
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th class="col-check">
                    <input type="checkbox" class="form-check-input" v-model="allSelected" @change="selectAll" />
                  </th>
                  <th>User</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Assigned Stores</th>
                  <th class="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(emp, index) in details"
                  :key="index"
                  :class="selectedModel.includes(emp.id) ? 'row-selected' : ''"
                >
                  <td class="col-check">
                    <input type="checkbox" class="form-check-input" v-model="selectedModel" :value="emp.id" />
                  </td>
  
                  <!-- User cell with avatar -->
                  <td>
                    <div class="user-cell">
                      <div class="user-avatar">
                        {{ (emp.first_name?.[0] ?? '') + (emp.last_name?.[0] ?? '') }}
                      </div>
                      <div>
                        <div class="user-name">{{ emp.first_name }} {{ emp.last_name }}</div>
                      </div>
                    </div>
                  </td>
  
                  <td class="text-muted-cell">{{ emp.email }}</td>
  
                  <td>
                    <span v-if="emp.roles?.[0]?.name" class="role-badge">
                      {{ emp.roles[0].name }}
                    </span>
                    <span v-else class="text-muted-cell">—</span>
                  </td>
  
                  <td>
                    <div class="store-tags" v-if="emp.stores?.length">
                      <span class="store-tag" v-for="st in emp.stores" :key="st.id">
                        {{ st.store_name }}
                      </span>
                    </div>
                    <span v-else class="text-muted-cell">—</span>
                  </td>
  
                  <td class="col-actions">
                    <div class="action-btns">
                      <router-link
                        :to="(permission.edit_employee == 0 || (emp.is_super == 1 && !isSuperAdmin)) ? '#' : { name: 'Edit User', params: { companyCode: $route.params.companyCode, id: emp.id } }"
                        class="action-btn action-btn--edit"
                        :class="(permission.edit_employee == 0 || (emp.is_super == 1 && !isSuperAdmin)) ? 'disabled' : ''"
                        :title="emp.is_super == 1 && !isSuperAdmin ? 'Only a Super Admin can edit a Super Admin user' : 'Edit user'"
                      >
                        <font-awesome-icon icon="pen" />
                      </router-link>
  
                      <button v-if="emp.is_super != 1"
                        class="action-btn action-btn--delete"
                        @click="getModalCurrentId(emp.id)"
                        :data-bs-toggle="company?.user_id !== emp.id && permission.delete_employee == 1 ? 'modal' : ''"
                        :data-bs-target="company?.user_id !== emp.id && permission.delete_employee == 1 ? '#singleModal' : ''"
                        :disabled="company?.user_id === emp.id || permission.delete_employee != 1"
                        title="Delete user"
                      >
                        <font-awesome-icon icon="trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
  
          <div v-if="pagination.lastPage > 1" class="pagination-bar">
            <span class="pagination-info">Showing {{ pagination.from }}–{{ pagination.to }} of {{ pagination.total }}</span>
            <div class="pagination-controls">
              <button class="pg-btn" :disabled="pagination.currentPage <= 1" @click="tableData.page = 1; fetchData()">«</button>
              <button class="pg-btn" :disabled="pagination.currentPage <= 1" @click="tableData.page = pagination.currentPage - 1; fetchData()">‹ Prev</button>
              <span class="pg-pages">
                <button v-for="p in pageRange" :key="p" class="pg-num" :class="{ 'pg-num--active': p === pagination.currentPage }" @click="tableData.page = p; fetchData()">{{ p }}</button>
              </span>
              <button class="pg-btn" :disabled="pagination.currentPage >= pagination.lastPage" @click="tableData.page = pagination.currentPage + 1; fetchData()">Next ›</button>
              <button class="pg-btn" :disabled="pagination.currentPage >= pagination.lastPage" @click="tableData.page = pagination.lastPage; fetchData()">»</button>
            </div>
          </div>
  
        </template>
      </div>
  
      <SingleDeleteModal
        :modalId="'singleModal'"
        @deleteSingle="callSingleEmployeeDeleteButton"
        :modalText="'Are you sure you want to delete this user?'"
        :action="'deleteSingle'"
      />
      <BulkDeleteModal
        :modalId="'bulkModal'"
        @deleteBulk="callBulkDeletStoreButton"
        :modalText="'Are you sure you want to delete the selected users?'"
      />
    </div>

    <!-- ── Export Column Picker ──────────────────────────────────────────── -->
    <transition name="fade">
      <div v-if="showExportModal" class="col-picker-backdrop" @click.self="showExportModal = false">
        <div class="col-picker-modal">
          <div class="col-picker-header">
            <div>
              <h3 class="col-picker-title">Select Columns</h3>
              <p class="col-picker-sub">Choose what to include in your {{ pendingExportFmt.toUpperCase() }} export</p>
            </div>
            <button class="col-picker-close" @click="showExportModal = false">
              <font-awesome-icon icon="times" />
            </button>
          </div>
          <div class="col-picker-actions">
            <button class="col-picker-link" @click="selectedExportCols = exportColumnDefs.map(c => c.key)">Select all</button>
            <span class="col-picker-sep">·</span>
            <button class="col-picker-link" @click="selectedExportCols = []">Deselect all</button>
            <span class="col-picker-count">{{ selectedExportCols.length }} of {{ exportColumnDefs.length }} selected</span>
          </div>
          <div class="col-picker-grid">
            <label v-for="col in exportColumnDefs" :key="col.key" class="col-picker-item">
              <input type="checkbox" :value="col.key" v-model="selectedExportCols" class="col-picker-check" />
              <span class="col-picker-label">{{ col.label }}</span>
            </label>
          </div>
          <div class="col-picker-footer">
            <button class="btn-ghost-cp" @click="showExportModal = false">Cancel</button>
            <button class="btn-primary-cp"
              :disabled="selectedExportCols.length === 0 || exportLoading"
              @click="doExport()">
              <font-awesome-icon
                :icon="exportLoading ? 'spinner' : pendingExportFmt === 'pdf' ? 'file-pdf' : 'file-csv'"
                :spin="exportLoading" />
              Export {{ pendingExportFmt.toUpperCase() }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </template>

  <script>
  import AdminHeader from "./administrationheadertab.vue";
  import axios from "axios";
  import { useToast } from "vue-toastification";
  import SingleDeleteModal from '../../../components/ActionModal.vue';
  import BulkDeleteModal from '../../../components/bulkDeleteModal.vue';
  import { reusableMixin } from "../../../lib/reusableMixin";
  import { useStore } from "vuex";
  import jsPDF from 'jspdf';
  import 'jspdf-autotable';
  
  export default {
    components: { AdminHeader, SingleDeleteModal, BulkDeleteModal },
    mixins: [reusableMixin],
  
    computed: {
      permission() {
        return this.store.state.getCurrentUserPermission;
      },
      isSuperAdmin() {
        return this.store.state.getLoggedInUser?.is_super == 1;
      },
      pageRange() {
        if (!this.pagination.lastPage) return [];
        const cur = this.pagination.currentPage, last = this.pagination.lastPage;
        const range = [];
        for (let i = Math.max(1, cur - 2); i <= Math.min(last, cur + 2); i++) range.push(i);
        return range;
      },
    },
  
    watch: {
      query() { this.fetchData(); }
    },
  
    created() {
      this.fetchData();
      this.getUserCompany();
    },
  
    data() {
      return {
        print_content: [],
        headerArrayColumn: ['S/N', 'Email', 'First Name', 'Last Name', 'Role', 'Assigned Store'],
        details: [],
        loading: false,
        query: null,
        exportLoading: false,
        showExportModal: false,
        pendingExportFmt: 'csv',
        selectedExportCols: [],
        exportColumnDefs: [
          { key: 'name',   label: 'Name',            pdfWidth: 160 },
          { key: 'email',  label: 'Email',            pdfWidth: 221 },
          { key: 'role',   label: 'Role',             pdfWidth: 160 },
          { key: 'stores', label: 'Assigned Stores',  pdfWidth: 220 },
        ],
        selectedModel: [],
        allSelected: false,
        tableData: { page: 1,
          draw: 0, length: 5, search: '',
          column: 0, dir: 'desc', id: '',
          status: [0, 1, 2, 3], query: ''
        },
        pagination: {
          lastPage: '', currentPage: '', total: '',
          lastPageUrl: '', nextPageUrl: '', prevPageUrl: '',
          from: '', to: ''
        },
        toast: useToast(),
        currentID: null,
        company: null,
        permission_status: null,
        is_permission: true,
        store: useStore(),
      };
    },
  
    methods: {
      // ── Export ──────────────────────────────────────────────────────────
      openExportModal(format) {
        this.pendingExportFmt   = format;
        this.selectedExportCols = this.exportColumnDefs.map(c => c.key);
        this.showExportModal    = true;
      },

      getRowValue(row, key) {
        switch (key) {
          case 'name':   return `${row.first_name || ''} ${row.last_name || ''}`.trim() || '—';
          case 'email':  return row.email || '—';
          case 'role':   return row.roles?.[0]?.name || '—';
          case 'stores': return (row.stores || []).map(s => s.store_name).join(', ') || '—';
          default:       return '—';
        }
      },

      async doExport() {
        if (!this.selectedExportCols.length) return;
        this.exportLoading   = true;
        this.showExportModal = false;
        try {
          const params = { ...this.tableData, page: 1, length: 10000 };
          const res    = await axios.get('/admin/employee', { params });
          const rows   = Array.isArray(res.data?.data?.data) ? res.data.data.data : [];
          const cols    = this.exportColumnDefs.filter(c => this.selectedExportCols.includes(c.key));
          const headers = cols.map(c => c.label);
          const body    = rows.map(row => cols.map(c => this.getRowValue(row, c.key)));
          const date    = new Date().toISOString().slice(0, 10);
          if (this.pendingExportFmt === 'csv') {
            const csv  = [headers, ...body]
              .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
              .join('\n');
            const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
            const url  = URL.createObjectURL(blob);
            const a    = document.createElement('a');
            a.href = url; a.download = `users-${date}.csv`; a.click();
            URL.revokeObjectURL(url);
          } else {
            const doc       = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
            const usable    = 761;
            const totalW    = cols.reduce((s, c) => s + c.pdfWidth, 0);
            const scale     = usable / totalW;
            const colStyles = {};
            cols.forEach((c, i) => { colStyles[i] = { cellWidth: c.pdfWidth * scale }; });
            doc.setFontSize(18); doc.setFont('helvetica', 'bold'); doc.setTextColor(26, 43, 69);
            doc.text('User List', 40, 46);
            doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(122, 144, 176);
            doc.text(`Exported ${new Date().toLocaleDateString('en-GB')}   ·   ${rows.length} record(s)`, 40, 62);
            doc.autoTable({
              startY: 78, head: [headers], body,
              styles: { fontSize: 7.5, cellPadding: 3.5, textColor: [26, 43, 69], overflow: 'ellipsize' },
              headStyles: { fillColor: [0, 74, 173], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
              alternateRowStyles: { fillColor: [248, 250, 252] },
              columnStyles: colStyles, tableWidth: usable, margin: { left: 40, right: 40 },
              didDrawPage(data) {
                doc.setFontSize(8); doc.setTextColor(192, 202, 214);
                doc.text(`Page ${data.pageNumber}`, doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 16, { align: 'center' });
              },
            });
            doc.save(`users-${date}.pdf`);
          }
        } catch { this.toast.error('Export failed. Please try again.'); }
        finally { this.exportLoading = false; }
      },

      dataToPrint(details) {
        this.print_content = details.map((user, idx) => ({
          sn: idx + 1,
          email: user.email,
          first_name: user.first_name,
          last_name: user.last_name,
          role: user.roles[0]?.name,
          store: user.stores.map(s => s.store_name).join(', '),
        }));
      },
  
      getUserCompany() {
        axios.get('/settings/company').then(res => {
          this.company = res.data.data.data;
        });
      },
  
      selectAll() {
        this.selectedModel = this.allSelected ? this.details.map(u => u.id) : [];
      },
  
      getModalCurrentId(id) { this.currentID = id; },
  
      fetchData(url = '/admin/employee') {
        this.loading = true;
        this.tableData.query = this.query;
        this.tableData.draw++;
        axios.get(url, { params: this.tableData })
          .then(response => {
            this.loading = false;
            this.details = response.data.data?.data ?? [];
            this.dataToPrint(this.details);
            this.configPagination(response.data.data);
          })
          .catch(error => {
            this.loading = false;
            this.is_permission = false;
            if (error?.response?.status == 403) {
              this.permission_status = 403;
              this.toast.error(error.response.data.message);
            }
          });
      },
  
      callSingleEmployeeDeleteButton() {
        axios.delete(`/admin/employee/${this.currentID}`)
          .then(() => {
            this.toast.success('User deleted successfully');
            this.selectedModel = [];
            this.fetchData();
          })
          .catch(error => {
            this.toast.error(error?.response?.data?.message ?? 'Something went wrong');
            if (error?.response?.status == 403) this.permission_status = 403;
          });
      },
  
      callBulkDeletStoreButton() {
        axios.post('/admin/employee/delete-bulk', { employees: this.selectedModel })
          .then(response => {
            this.toast.success(response.data.message);
            this.selectedModel = [];
            this.fetchData();
          })
          .catch(error => {
            this.toast.error(error?.response?.data?.message ?? 'Something went wrong');
          });
      },
  
      configPagination(data) {
        this.pagination.lastPage    = data.last_page;
        this.pagination.currentPage = data.current_page;
        this.pagination.total       = data.total;
        this.pagination.lastPageUrl = data.last_page_url;
        this.pagination.nextPageUrl = data.next_page_url;
        this.pagination.prevPageUrl = data.prev_page_url;
        this.pagination.from        = data.from;
        this.pagination.to          = data.to;
      },
    },
  };
  </script>
  
  <style scoped>
  /* ── Page Header ── */
  .page-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 14px;
    margin-bottom: 20px;
    padding: 0 4px;
  }
  .page-title { font-size: 20px; font-weight: 700; color: #1a2b45; margin: 0 0 3px; }
  .page-sub   { font-size: 13px; color: #7a90b0; margin: 0; }
  
  .page-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }
  
  /* ── Search ── */
  .search-wrap { position: relative; display: flex; align-items: center; }
  .search-icon {
    position: absolute; left: 11px;
    color: #7a90b0; font-size: 13px; pointer-events: none;
  }
  .search-input {
    padding: 8px 12px 8px 32px;
    border: 1px solid #dde5f0; border-radius: 8px;
    font-size: 13px; color: #1a2b45; background: #fff;
    width: 220px; outline: none; transition: border-color 0.15s;
  }
  .search-input:focus { border-color: #004AAD; }
  .search-input::placeholder { color: #aab8cc; }
  
  /* ── Buttons ── */
  .btn-primary-action {
    display: inline-flex; align-items: center; gap: 7px;
    background: #004AAD; color: #fff; font-size: 13px; font-weight: 600;
    padding: 8px 16px; border-radius: 8px; border: none;
    text-decoration: none; cursor: pointer; transition: background 0.15s; white-space: nowrap;
  }
  .btn-primary-action:hover { background: #003b8e; color: #fff; text-decoration: none; }
  .btn-primary-action.disabled { opacity: 0.5; pointer-events: none; }
  
  .btn-danger-action {
    display: inline-flex; align-items: center; gap: 7px;
    background: #fff0f0; color: #e03535; border: 1px solid #fac5c5;
    font-size: 13px; font-weight: 600; padding: 8px 16px;
    border-radius: 8px; cursor: pointer; transition: background 0.15s; white-space: nowrap;
  }
  .btn-danger-action:hover:not(:disabled) { background: #ffe0e0; }
  .btn-danger-action:disabled { opacity: 0.45; cursor: not-allowed; }
  
  .btn-outline {
    display: inline-flex; align-items: center; gap: 7px;
    background: #fff; color: #1a2b45; border: 1px solid #dde5f0;
    font-size: 13px; font-weight: 500; padding: 8px 14px;
    border-radius: 8px; cursor: pointer; transition: background 0.15s; white-space: nowrap;
  }
  .btn-outline:hover { background: #f4f7fb; }
  .btn-outline::after { display: none !important; }
  
  .badge-count {
    display: inline-flex; align-items: center; justify-content: center;
    background: #e03535; color: #fff; font-size: 10px; font-weight: 700;
    width: 18px; height: 18px; border-radius: 50%;
  }
  
  /* ── Data Card ── */
  .data-card {
    background: #fff;
    border: 1px solid #e4eaf2;
    border-radius: 12px;
    overflow: hidden;
  }
  
  /* ── States ── */
  .state-block {
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    padding: 64px 24px; text-align: center;
  }
  .state-icon {
    width: 60px; height: 60px; border-radius: 16px;
    display: flex; align-items: center; justify-content: center;
    font-size: 24px; margin-bottom: 16px;
  }
  .state-icon--blue { background: rgba(0,74,173,0.1); color: #004AAD; }
  .state-icon--red  { background: rgba(224,53,53,0.1); color: #e03535; }
  .state-title { font-size: 16px; font-weight: 700; color: #1a2b45; margin: 0 0 6px; }
  .state-sub   { font-size: 13px; color: #7a90b0; margin: 0; }
  
  /* ── Toolbar ── */
  .table-toolbar {
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 20px; border-bottom: 1px solid #e4eaf2;
    flex-wrap: wrap; gap: 10px;
  }
  .table-count { font-size: 13px; color: #7a90b0; }
  .rows-select {
    border: 1px solid #dde5f0; border-radius: 7px;
    padding: 5px 10px; font-size: 13px; color: #1a2b45;
    background: #f8fafc; outline: none; cursor: pointer;
  }
  .rows-select:focus { border-color: #004AAD; }
  
  /* ── Table ── */
  .table-wrap { overflow-x: auto; }
  .data-table { width: 100%; border-collapse: collapse; }
  
  .data-table thead tr { background: #f8fafc; }
  .data-table thead th {
    font-size: 11.5px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.05em;
    color: #7a90b0; padding: 12px 16px;
    border-bottom: 1px solid #e4eaf2; white-space: nowrap;
  }
  
  .data-table tbody tr { border-bottom: 1px solid #f0f4f8; transition: background 0.12s; }
  .data-table tbody tr:last-child { border-bottom: none; }
  .data-table tbody tr:hover { background: #f8fafc; }
  .data-table tbody tr.row-selected { background: #edf4ff; }
  
  .data-table tbody td { padding: 12px 16px; font-size: 13.5px; vertical-align: middle; }
  
  .col-check   { width: 44px; text-align: center; }
  .col-actions { width: 90px; text-align: center; }
  .text-muted-cell { color: #4a5e78; font-size: 13px; }
  
  /* ── User Cell ── */
  .user-cell { display: flex; align-items: center; gap: 10px; }
  .user-avatar {
    width: 34px; height: 34px; border-radius: 50%;
    background: #004AAD; color: #fff;
    display: flex; align-items: center; justify-content: center;
    font-size: 12px; font-weight: 700;
    flex-shrink: 0; text-transform: uppercase;
  }
  .user-name { font-weight: 600; color: #1a2b45; font-size: 13.5px; }
  
  /* ── Role Badge ── */
  .role-badge {
    display: inline-block;
    background: rgba(0,74,173,0.08);
    color: #004AAD;
    border: 1px solid rgba(0,74,173,0.15);
    border-radius: 20px;
    padding: 3px 10px;
    font-size: 12px;
    font-weight: 600;
    text-transform: capitalize;
    white-space: nowrap;
  }
  
  /* ── Store Tags ── */
  .store-tags { display: flex; flex-wrap: wrap; gap: 4px; }
  .store-tag {
    display: inline-block;
    background: #f4f7fb;
    color: #4a5e78;
    border: 1px solid #dde5f0;
    border-radius: 5px;
    padding: 2px 8px;
    font-size: 11.5px;
    font-weight: 500;
    white-space: nowrap;
  }
  
  /* ── Action Buttons ── */
  .action-btns { display: flex; align-items: center; justify-content: center; gap: 6px; }
  .action-btn {
    width: 32px; height: 32px; border-radius: 7px;
    border: 1px solid #e4eaf2; background: #fff;
    display: flex; align-items: center; justify-content: center;
    font-size: 13px; cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
    text-decoration: none;
  }
  .action-btn--edit  { color: #004AAD; }
  .action-btn--edit:hover:not(.disabled) { background: #edf4ff; border-color: #c5d6f5; }
  .action-btn--delete { color: #e03535; }
  .action-btn--delete:hover:not(:disabled) { background: #fff0f0; border-color: #fac5c5; }
  .action-btn--delete:disabled { color: #ccc; cursor: not-allowed; border-color: #f0f0f0; }
  .action-btn.disabled { color: #ccc; pointer-events: none; }
  
  /* ── Footer ── */
  .table-footer {
    padding: 14px 20px; border-top: 1px solid #e4eaf2;
    display: flex; justify-content: flex-end;
  }
  .pagination-bar      { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-top:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
  .pagination-info     { font-size:13px; color:#7a90b0; }
  .pagination-controls { display:flex; align-items:center; gap:4px; }
  .pg-btn  { padding:6px 11px; border:1px solid #dde5f0; border-radius:7px; background:#fff; color:#1a2b45; font-size:13px; cursor:pointer; transition:all .12s; }
  .pg-btn:hover:not(:disabled) { background:#f4f7fb; border-color:#c8d6e8; }
  .pg-btn:disabled { opacity:.4; cursor:not-allowed; }
  .pg-pages { display:flex; gap:3px; }
  .pg-num  { min-width:34px; padding:6px 8px; border:1px solid #dde5f0; border-radius:7px; background:#fff; color:#1a2b45; font-size:13px; cursor:pointer; transition:all .12s; text-align:center; }
  .pg-num:hover { background:#f4f7fb; border-color:#c8d6e8; }
  .pg-num--active { background:#004AAD; border-color:#004AAD; color:#fff; font-weight:600; }
  
  /* ── Dropdown ── */
  .dropdown-menu {
    border: 1px solid #e4eaf2; border-radius: 9px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.08);
    padding: 6px; min-width: 170px;
  }
  .dropdown-item {
    border-radius: 6px; font-size: 13px;
    padding: 8px 12px; color: #1a2b45; transition: background 0.12s;
  }
  .dropdown-item:hover { background: #f4f7fb; }
  
  /* ── Responsive ── */
  @media (max-width: 767px) {
    .page-header  { flex-direction: column; align-items: stretch; }
    .page-actions { flex-direction: column; }
    .search-input { width: 100%; }
    .btn-primary-action,
    .btn-danger-action,
    .btn-outline  { width: 100%; justify-content: center; }
  }

  /* ── Column picker modal ──────────────────────────────────────────────── */
  .col-picker-backdrop { position:fixed; inset:0; background:rgba(15,25,40,.45); z-index:1050; display:flex; align-items:center; justify-content:center; padding:20px; }
  .col-picker-modal    { background:#fff; border-radius:14px; width:100%; max-width:480px; box-shadow:0 24px 60px rgba(0,0,0,.18); overflow:hidden; }
  .col-picker-header   { display:flex; align-items:flex-start; justify-content:space-between; padding:20px 22px 14px; border-bottom:1px solid #f0f4f9; }
  .col-picker-title    { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .col-picker-sub      { font-size:12px; color:#7a90b0; margin:0; }
  .col-picker-close    { background:none; border:none; color:#7a90b0; font-size:15px; cursor:pointer; padding:4px; line-height:1; }
  .col-picker-close:hover { color:#1a2b45; }
  .col-picker-actions  { display:flex; align-items:center; gap:8px; padding:10px 22px; background:#f8fafc; font-size:12px; border-bottom:1px solid #f0f4f9; }
  .col-picker-link     { background:none; border:none; color:#004AAD; font-size:12px; cursor:pointer; padding:0; font-weight:600; font-family:inherit; }
  .col-picker-link:hover { text-decoration:underline; }
  .col-picker-sep      { color:#c5d3e0; }
  .col-picker-count    { margin-left:auto; color:#7a90b0; font-size:11.5px; }
  .col-picker-grid     { display:grid; grid-template-columns:1fr 1fr; gap:2px; padding:14px 16px; max-height:280px; overflow-y:auto; }
  .col-picker-item     { display:flex; align-items:center; gap:9px; padding:9px 10px; border-radius:8px; cursor:pointer; transition:background .1s; }
  .col-picker-item:hover { background:#f4f7fb; }
  .col-picker-check    { width:15px; height:15px; accent-color:#004AAD; cursor:pointer; flex-shrink:0; }
  .col-picker-label    { font-size:13px; color:#1a2b45; user-select:none; }
  .col-picker-footer   { display:flex; justify-content:flex-end; gap:8px; padding:14px 20px; border-top:1px solid #f0f4f9; }
  .btn-ghost-cp   { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:1px solid #e4eaf2; cursor:pointer; font-family:inherit; }
  .btn-ghost-cp:hover { background:#e4eaf2; }
  .btn-primary-cp { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
  .btn-primary-cp:hover:not(:disabled) { background:#003b8e; }
  .btn-primary-cp:disabled { opacity:.45; cursor:not-allowed; }
  .fade-enter-active, .fade-leave-active { transition:opacity .15s ease; }
  .fade-enter, .fade-leave-to { opacity:0; }
  </style>